/**
 * The Rezervace grid's filter state, as pure functions over a URLSearchParams.
 *
 * This module imports NOTHING — same discipline as `api/src/read/sql.ts` and
 * `shared/src/rules.ts`, and for a related reason: this is the code that
 * decides which reservations an operator is shown. The bug it exists to
 * prevent already happened once. A search was silently intersected with the
 * default date window, so pasting a booking number for a customer arriving in
 * September returned "no reservations match" — and the dispatcher on the
 * phone would have told that customer their booking did not exist.
 *
 * That class of mistake is invisible in a screenshot and obvious in an
 * assertion, so `scripts/verify-web-filters.mjs` executes this file directly
 * with no packages installed. Where the functions need to know the legal
 * status and sort values, they take them as an argument rather than importing
 * the Zod schemas — the same config seam `session-token.ts` uses, and what
 * keeps this file loadable by a harness.
 */

export interface Filters {
  q: string;
  /** Stav: a legacy group ('active', 'waiting', 'notdeleted') or single statuses. */
  status: string[];
  from: string;
  to: string;
  dateField: DateField;
  /** Parking: cheap / regular / exclusive, or '' for all. */
  type: string;
  /** "Vyžaduje pozornost administrátora!" */
  needadmin: boolean;
  /** Partner: the account id as it stands in the URL, '' for all. */
  partnerId: string;
  /** Extra služby: catalogue ids, any of. */
  services: number[];
  /** Smile Club: '' or one of CLUB_FILTERS. */
  club: string;
  /** Web (zdroj): '' or one of SOURCE_FILTERS. */
  source: string;
  unpaidOnly: boolean;
  missingCarOnly: boolean;
  /**
   * Where the car is standing: a site key, 'none' for the unattributed
   * bucket, or '' for no filter at all.
   *
   * The empty string is deliberately not a member of the catalogue it is
   * validated against — same shape as Klienti's Stav control, and for the
   * same reason: "no filter" must never become a value the API is asked to
   * match. See `oneOf` below, whose second type parameter exists for this.
   */
  site: string;
  sort: string;
  dir: 'asc' | 'desc';
  page: number;
  perPage: number;
}

export type DateField = 'arrival' | 'departure' | 'created';

/** The legacy selector beside Datum, its words: rezervace / příjezd / odjezd. */
export const DATEFIELD_LABEL: Record<DateField, string> = {
  created: 'rezervace',
  arrival: 'příjezd',
  departure: 'odjezd',
};
export const DATE_FIELDS: readonly DateField[] = ['created', 'arrival', 'departure'];

/**
 * The legacy default window when its date box was empty:
 * getInterval('last3months') on `created` — three months back to today.
 * Kept for the harness and for anyone comparing counts against the old page;
 * the screen itself opens on today since 4 October (see makeDefaults).
 */
export const RESERVATION_LOOKBACK_MONTHS = 3;
export const RESERVATION_PER_PAGE = 20;

/** 'YYYY-MM-DD' `months` calendar months before `now`, clamped to the month's end. */
export function monthsAgoISO(months: number, now: Date = new Date()): string {
  const d = new Date(now.getTime());
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() - months);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** The vocabularies live in @smile/shared; the caller passes them in. */
export interface Catalogue {
  statuses: readonly string[];
  sorts: readonly string[];
  /**
   * MAX_PAGE from the shared rules — the same constant the Zod schemas cap
   * `page` with. Required rather than optional on purpose: a screen that
   * forgets it is a screen that can put a page number in the address bar which
   * its own API refuses, and a required field makes the compiler say so at the
   * call site instead of leaving it to be discovered from a bookmark.
   */
  maxPage: number;
  /**
   * The legal `site` values — PARKING_SITES plus 'none', which @smile/shared
   * exports as SITE_FILTERS. Passed in for the same reason the statuses are:
   * this file imports nothing, and the list the screen passes is the same
   * list the Zod enum is built from, so the two cannot drift.
   */
  sites: readonly string[];
  /** CLUB_FILTERS and SOURCE_FILTERS from @smile/shared, for the same reason. */
  clubs: readonly string[];
  sources: readonly string[];
  types: readonly string[];
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 'YYYY-MM-DD', offset from today, in LOCAL time.
 *
 * Never `toISOString().slice(0, 10)`: that converts to UTC first, so between
 * midnight and 02:00 Prague time it returns yesterday. The night shift would
 * open the screen to a window one day behind the one they are working.
 */
export function todayISO(offsetDays = 0, now: Date = new Date()): string {
  const d = new Date(now.getTime());
  d.setDate(d.getDate() + offsetDays);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Computed on demand, not at module load: a browser left open overnight
 * would otherwise still be defaulting to yesterday's window.
 */
export function makeDefaults(now?: Date): Filters {
  return {
    q: '',
    status: [],
    // The owner's ruling (4 October): the page opens on TODAY — bookings made
    // today, newest first, twenty to a page. The legacy page opened on the
    // last three months by creation date ("FILTER date: empty = last 30 days,
    // performance reasons" — the code said three months), which counted
    // 12 000-odd rows for nothing; a wider window is one date-box edit away,
    // and the Celkem count still matches the old page for the same dates.
    from: todayISO(0, now),
    to: todayISO(0, now),
    dateField: 'created',
    type: '',
    needadmin: false,
    partnerId: '',
    services: [],
    club: '',
    source: '',
    unpaidOnly: false,
    missingCarOnly: false,
    // No site filter. The grid opens on every yard, because a dispatcher
    // looking for a booking does not yet know which yard the car is in.
    site: '',
    sort: 'created',
    dir: 'desc',
    page: 1,
    perPage: RESERVATION_PER_PAGE,
  };
}

/* ------------------------------------------------------------------ *
 * Quick ranges
 * ------------------------------------------------------------------ */

/**
 * The date range as a pair of local 'YYYY-MM-DD' strings.
 *
 * Every one of these is computed from a `Date` passed in, never from a clock
 * read inside the function, because a range that cannot be asked "what would
 * you have said on the 31st of January" is a range whose month arithmetic
 * nobody has checked.
 */
export interface DateRange {
  from: string;
  to: string;
}

function ymd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Monday of the week containing `now`, in local time.
 *
 * `getDay()` numbers Sunday 0, which is the American week. Prague's week
 * starts on Monday, so Sunday has to step back six days rather than forward
 * one — get this wrong and the "Tento týden" button shows a dispatcher
 * working a Sunday night shift the week that starts tomorrow.
 */
function mondayOf(now: Date): Date {
  const d = new Date(now.getTime());
  const dow = d.getDay();
  d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  return d;
}

function shiftDays(now: Date, days: number): Date {
  const d = new Date(now.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * The buttons beside the date range.
 *
 * They exist because the screen shipped with two bare `<input type="date">`
 * controls and nothing else, and the owner's note was exactly that: "the
 * filters also do not have quick buttons". Picking "today" out of a native
 * date picker is four clicks and a read of the month header; on a shift
 * change with a queue at the desk, nobody does it twice.
 *
 * Each entry is a pure function of `now`, which is what lets the harness ask
 * what "tento měsíc" means on the 31st of January without waiting for
 * January.
 */
export interface QuickRange {
  key: string;
  label: string;
  /** The tooltip: the same thing said in words, for the hesitant. */
  title: string;
  range: (now: Date) => DateRange;
}

export const QUICK_RANGES: readonly QuickRange[] = [
  {
    key: 'dnes',
    label: 'Dnes',
    title: 'Dnešní den',
    range: (now) => ({ from: ymd(now), to: ymd(now) }),
  },
  {
    key: 'zitra',
    label: 'Zítra',
    title: 'Zítřejší den',
    range: (now) => ({ from: ymd(shiftDays(now, 1)), to: ymd(shiftDays(now, 1)) }),
  },
  {
    key: 'vcera',
    label: 'Včera',
    title: 'Včerejší den',
    range: (now) => ({ from: ymd(shiftDays(now, -1)), to: ymd(shiftDays(now, -1)) }),
  },
  {
    key: 'tyden',
    label: 'Tento týden',
    title: 'Pondělí až neděle tohoto týdne',
    range: (now) => {
      const mon = mondayOf(now);
      return { from: ymd(mon), to: ymd(shiftDays(mon, 6)) };
    },
  },
  {
    key: 'dnu7',
    label: 'Příštích 7 dní',
    title: 'Ode dneška o týden dopředu',
    range: (now) => ({ from: ymd(now), to: ymd(shiftDays(now, 7)) }),
  },
  {
    key: 'mesic',
    label: 'Tento měsíc',
    title: 'Od prvního do posledního dne tohoto měsíce',
    range: (now) => ({
      from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)),
      /* Day 0 of the next month is the last day of this one. Written this way
         rather than as "add 30 days" because February exists. */
      to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    }),
  },
];

/**
 * The same strip for the screens that look backwards.
 *
 * Rezervace and the dashboard plan a shift, so their buttons lean forward:
 * Zítra, Příštích 7 dní. Statistiky and Uzávěrky report on one that has
 * happened, and "Zítra" on a revenue chart draws an empty box. Offering the
 * forward set there would not be consistency, it would be six buttons of
 * which two are always blank — and a control that is sometimes pointless is
 * one people stop reading.
 *
 * What stays identical is everything a person has to learn: the strip sits in
 * the same place in the toolbar, looks the same, highlights the one that is
 * active, and "Dnes" and "Tento měsíc" mean on both screens exactly what they
 * mean on Rezervace. Only the entries that could not apply are swapped.
 *
 * "Minulý měsíc" is here and not on Rezervace because it is the single most
 * asked question of a reporting screen and nobody has ever asked a dispatch
 * queue about it.
 */
export const PAST_QUICK_RANGES: readonly QuickRange[] = [
  {
    key: 'dnes',
    label: 'Dnes',
    title: 'Dnešní den',
    range: (now) => ({ from: ymd(now), to: ymd(now) }),
  },
  {
    key: 'vcera',
    label: 'Včera',
    title: 'Včerejší den',
    range: (now) => ({ from: ymd(shiftDays(now, -1)), to: ymd(shiftDays(now, -1)) }),
  },
  {
    key: 'zpet7',
    label: 'Posledních 7 dní',
    title: 'Od dneška o týden zpět',
    range: (now) => ({ from: ymd(shiftDays(now, -7)), to: ymd(now) }),
  },
  {
    key: 'zpet14',
    label: 'Posledních 14 dní',
    title: 'Od dneška o dva týdny zpět',
    range: (now) => ({ from: ymd(shiftDays(now, -14)), to: ymd(now) }),
  },
  {
    key: 'zpet30',
    label: 'Posledních 30 dní',
    title: 'Od dneška o třicet dní zpět',
    range: (now) => ({ from: ymd(shiftDays(now, -30)), to: ymd(now) }),
  },
  {
    key: 'mesic',
    label: 'Tento měsíc',
    title: 'Od prvního dne měsíce do dneška',
    /* Ends TODAY, not on the last day of the month — which is where this
       entry differs from its namesake in QUICK_RANGES, and deliberately.
       Rezervace looks forwards and a month that runs to the 31st is exactly
       what is wanted there. These two screens look backwards: asked on the
       4th, a window ending on the 31st gives Statistiky a chart with
       twenty-seven empty bars on the end, which reads as business falling off
       a cliff rather than as days that have not happened yet. */
    range: (now) => ({
      from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: ymd(now),
    }),
  },
  {
    key: 'mesicMinuly',
    label: 'Minulý měsíc',
    title: 'Celý předchozí kalendářní měsíc',
    range: (now) => ({
      /* Month -1 with day 1, and day 0 of the current month for the end.
         `new Date(2026, -1, 1)` is December 2025 — the constructor rolls the
         year for you, which is why this is not written as an if. */
      from: ymd(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: ymd(new Date(now.getFullYear(), now.getMonth(), 0)),
    }),
  },
];

/**
 * Which button, if any, describes the range currently in the URL.
 *
 * Without this the buttons are write-only: you press "Dnes", the grid changes,
 * and the strip goes back to looking exactly as it did, so the screen never
 * tells you what window you are on. It also has to return null for a range
 * that matches nothing — including the screen's own default — rather than
 * guessing at the nearest one.
 *
 * The list is an argument with the forward set as its default, so the two
 * strips share one implementation and neither screen keeps its own copy of
 * "is this button the current one". Screens that pass PAST_QUICK_RANGES pass
 * the same list to both the map and to this, which is the invariant
 * verify:web asserts: a strip cannot highlight a button it does not render.
 */
export function quickRangeKey(
  f: Pick<Filters, 'from' | 'to'>,
  now: Date,
  ranges: readonly QuickRange[] = QUICK_RANGES,
): string | null {
  for (const q of ranges) {
    const r = q.range(now);
    if (r.from === f.from && r.to === f.to) return q.key;
  }
  return null;
}

/**
 * How far back Statistiky looks when the URL says nothing.
 *
 * Rezervace opens on yesterday-to-a-fortnight-out, which is the working set a
 * dispatcher needs. On Statistiky that window would draw a manager a chart of
 * bookings that have not happened yet, so the screen looks back a month
 * instead. Both defaults are right for their own screen.
 */
export const STATS_LOOKBACK_DAYS = 30;

/**
 * How long a search box waits after the last keystroke before it searches.
 *
 * One number for the three screens that have a free-text box, because a
 * person does not learn "the search box" once per screen. Rezervace and
 * Statistiky already typed-to-search; Klienti did not — it searched on Enter,
 * and also, silently, on clicking away, which is the worse half: the result
 * set changed because of a click that was about something else entirely.
 * Staff here cannot be retrained screen by screen, so the control behaves the
 * same way in all three places.
 *
 * 350 ms is above a fast typist's gap between keystrokes and below the point
 * where a person starts to wonder whether the box is working.
 */
export const SEARCH_DEBOUNCE_MS = 350;

/**
 * The Statistiky-only overrides to apply on top of parseFilters().
 *
 * The override applies ONLY when the URL carries no date at all. That is what
 * keeps the two screens' links interchangeable: a filtered view pasted from
 * Rezervace lands on exactly the window it was sharing, and only a bare
 * /statistiky gets the month-back default.
 */
export function statsDefaults(sp: URLSearchParams, now?: Date): Partial<Filters> {
  // Statistiky keeps reading its window against the arrival date unless the
  // link says otherwise; the grid's legacy default of `created` is the grid's.
  const field: Partial<Filters> = sp.has('dateField') ? {} : { dateField: 'arrival' };
  if (sp.has('from') || sp.has('to')) return field;
  return { ...field, from: todayISO(-STATS_LOOKBACK_DAYS, now), to: todayISO(0, now) };
}

export type StatsUnit = 'auto' | 'day' | 'month';
export const STATS_UNITS: readonly StatsUnit[] = ['auto', 'day', 'month'];

/**
 * The chart granularity out of the address bar.
 *
 * It gets the same treatment every other parameter in this file gets, and for
 * the same reason stated at the top: nothing here trusts the input. This one
 * was the exception for a while — the screen cast `sp.get('unit')` straight to
 * the union and sent it on — so a hand-edited or truncated `?unit=d` reached
 * the API, came back 400, and left an error card with no control on the page
 * able to clear it. A value nobody recognises is 'auto', which is the value a
 * URL that says nothing already means.
 */
export function statsUnit(raw: string | null | undefined): StatsUnit {
  return oneOf(raw, STATS_UNITS, 'auto');
}

/**
 * The general form of the rule statsUnit is one instance of.
 *
 * The address bar is user input. Every screen in this application reads its
 * state out of it, and a value that reaches the API without being recognised
 * comes back 400 — which on a screen whose only escape hatch is "Zrušit
 * filtry" is a dead end, because that button clears the filters and not the
 * parameter that broke. Klienti had exactly this shape: `?razeni=neco`
 * produced a permanently broken screen that no control on the page could
 * repair, and the operator's only recovery was to know to edit the URL.
 *
 * The allowed list is passed in rather than imported, so this file keeps its
 * no-imports property and the caller stays the single owner of what is legal
 * — the list it passes is the same `as const` tuple the Zod schema is built
 * from, so the two cannot drift.
 *
 * Note the fallback is a value, not `undefined`: a parameter nobody
 * recognises should behave like a parameter nobody supplied.
 *
 * The fallback has its own type parameter because "no filter" is often not
 * one of the legal filter values — Klienti's Stav control falls back to the
 * empty string, which is not a member of CLIENT_STATUS_FILTERS and must not
 * be, or the empty string would become something the API is asked to match.
 * The return type is the union of the two, so the caller still cannot receive
 * a value neither list contains.
 */
export function oneOf<T extends string, F = T>(
  raw: string | null | undefined,
  allowed: readonly T[],
  fallback: F,
): T | F {
  return allowed.includes(raw as T) ? (raw as T) : fallback;
}

/**
 * The same rule for a number in the address bar: a rows-per-page that is not
 * one of the sizes the control offers is treated as absent.
 *
 * `includes` on the offered list is what does the work, and it does more of it
 * than it looks like it does. A hand-edited `?radku=1000` is not a filter that
 * returns nothing — it is a 400 from an API whose schema caps this endpoint at
 * 100, on a screen where "Zrušit filtry" does not clear `radku`, so the error
 * would have no way out but the address bar. Everything that is not on the list
 * has to land on the fallback, and `Number` produces a great deal that is not:
 * NaN from 'abc', Infinity from 'Infinity', 50.5 from '50.5', and 50 from both
 * '5e1' and ' 50 '. The last two are accepted, because they are 50.
 *
 * The blank guard on the first line is the part that is not obvious, and it is
 * there for a caller this file does not have yet. `Number('')`, `Number('  ')`
 * and `Number(null)` are all **0** rather than NaN, so with no guard an absent
 * parameter arrives as a deliberate zero. That is harmless while every list of
 * sizes starts at 25 — 0 is not on it either way — and it is a silent wrong
 * answer the first time some list contains 0 as a legal value. Cheaper to hold
 * the line here than to find it there.
 *
 * This function used to also test `Number.isInteger` before consulting the
 * list. That check was removed rather than kept as belt-and-braces: for a list
 * of integers it can only ever reject a value the list was going to reject
 * anyway, and for a list containing 0.5 it would reject a value that is on the
 * list. A guard that cannot fail is a guard that cannot be tested, and this
 * project does not keep those.
 */
export function oneOfNum<F = number>(
  raw: string | null | undefined,
  allowed: readonly number[],
  fallback: F,
): number | F {
  if (raw == null || raw.trim() === '') return fallback;
  const n = Number(raw);
  return allowed.includes(n) ? n : fallback;
}

/**
 * A page number out of the address bar, as a page number the API will accept.
 *
 * Three screens used to read this three different ways, and two of the three
 * could hand the server a value its own schema rejects. Klienti and Uzávěrky
 * both did `Math.max(1, Number(sp.get('strana') || 1) || 1)`, which is right
 * about 'abc' and about '-3' and wrong about everything in between: '2.5' went
 * through as 2.5 and `z.coerce.number().int()` refuses it; '1e9' went through
 * as a billion and the schema caps at MAX_PAGE; 'Infinity' went through as the
 * literal string "Infinity". Rezervace floored and required ≥ 1 but never
 * looked at the ceiling, so it had the third of those.
 *
 * None of that was unrecoverable — every one of these screens clears `strana`
 * on the next filter change, so a range button or a checkbox gets you out. It
 * is worth being exact about that, because the failure is not "the operator is
 * stuck". The failure is what the screen says while it happens: *"Uzávěrky se
 * nepodařilo načíst"*, which describes a server that is down. Somebody rings
 * about an outage, and the outage is a stale bookmark.
 *
 * `max` is passed in rather than imported, for the reason the header of this
 * file gives — and because the ceiling belongs to the Zod schema, so the
 * screen should be quoting it rather than keeping its own copy.
 *
 * There is deliberately no `raw == null || raw.trim() === ''` guard here, the
 * one `oneOfNum` above needs. `Number(null)`, `Number('')` and `Number('  ')`
 * are all 0, 0 fails `n < 1`, and the answer is 1 — which is the answer the
 * guard would have given. Writing it anyway would put a line in this file that
 * no test could ever turn red, and the round before this one was spent
 * removing exactly that.
 */
export function pageParam(raw: string | null | undefined, max: number): number {
  const n = Number(raw);
  // NaN fails `isFinite` and would slip past `n < 1`, which is false for NaN.
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(max, Math.floor(n));
}

/**
 * A 'YYYY-MM-DD' out of the address bar, or the default.
 *
 * Shape only, matching the schema: `BalanceQuery` tests the same regex and
 * nothing more, so '2026-13-45' is accepted here exactly as it is accepted
 * there. That is not an oversight in either place. A date of the right shape
 * that does not exist compares as a string against stored datetimes and
 * returns an empty period — a screen that says no shift was closed, which is
 * true of 45 December. A date of the wrong shape returns a 400.
 *
 * `parseFilters` keeps its own copy of this rule rather than calling here,
 * because the Rezervace grid gives `?from=` with nothing after it a meaning:
 * an explicit blank is "no lower bound", which is different from an absent
 * one. Uzávěrky has no such concept — it always sends both bounds — so there
 * a blank is simply a value that is not a date. Two rules that look alike and
 * are not; merging them would silently give one screen the other's behaviour.
 *
 * `raw != null` is TypeScript's requirement and not a runtime guard: the
 * regex is anchored, and `DATE_RE.test(null)` tests the four-character string
 * "null" and is false already. Said out loud so that nobody writes a test
 * against it believing it proves something — it cannot be made to fail.
 */
export function dateParam(raw: string | null | undefined, fallback: string): string {
  return raw != null && DATE_RE.test(raw) ? raw : fallback;
}

/**
 * Read filters out of the address bar.
 *
 * Nothing here trusts the input: every value is either recognised or falls
 * back to the default. A hand-edited URL should give a sane screen, not a
 * 400 from the API and an error state the operator cannot get out of.
 */
export function parseFilters(sp: URLSearchParams, cat: Catalogue, now?: Date): Filters {
  const d = makeDefaults(now);
  const str = (k: string, fb: string) => sp.get(k) ?? fb;
  const int = (k: string, fb: number) => {
    const raw = sp.get(k);
    if (raw === null || raw.trim() === '') return fb;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : fb;
  };
  const date = (k: string, fb: string) => {
    const v = sp.get(k);
    // An explicit blank means "no bound", which is different from "absent".
    if (v === '') return '';
    return v !== null && DATE_RE.test(v) ? v : fb;
  };

  const dateField = str('dateField', d.dateField);
  const sort = str('sort', d.sort);
  const q = str('q', '');

  /* A link that carries a search term and no dates — the dashboard's and
     Převozy's search boxes send `/rezervace?q=…` — means "find this
     anywhere", so the three-month default is not filled in behind it. A
     search typed on the grid itself runs inside whatever the date box shows,
     exactly as the old page's Hledat did; clearing the box widens it. */
  const linkedSearch = q.trim() !== '' && !sp.has('from') && !sp.has('to');

  return {
    q,
    status: sp.getAll('status').filter((s) => cat.statuses.includes(s)),
    from: linkedSearch ? '' : date('from', d.from),
    to: linkedSearch ? '' : date('to', d.to),
    dateField: dateField in DATEFIELD_LABEL ? (dateField as DateField) : d.dateField,
    type: oneOf(sp.get('type'), cat.types, ''),
    needadmin: sp.get('needadmin') === '1',
    partnerId: /^\d{1,10}$/.test(sp.get('partnerId') ?? '') ? (sp.get('partnerId') as string) : '',
    services: sp
      .getAll('services')
      .filter((v) => /^\d{1,10}$/.test(v))
      .map(Number),
    club: oneOf(sp.get('club'), cat.clubs, ''),
    source: oneOf(sp.get('source'), cat.sources, ''),
    unpaidOnly: sp.get('unpaidOnly') === '1',
    missingCarOnly: sp.get('missingCarOnly') === '1',
    // Falls back to '' — no filter — for anything the catalogue does not
    // contain, so `?site=letiste` gives every yard rather than a 400.
    site: oneOf(sp.get('site'), cat.sites, ''),
    sort: cat.sorts.includes(sort) ? sort : d.sort,
    dir: str('dir', d.dir) === 'desc' ? 'desc' : 'asc',
    // Was `int('page', 1)`, which floored and refused anything below 1 but
    // never looked at the ceiling — so `?page=99999999` was forwarded intact
    // and came back 400. See pageParam.
    page: pageParam(sp.get('page'), cat.maxPage),
    // The API rejects anything outside 10..500 with a 400; clamp rather than
    // let a stale bookmark render an unrecoverable error.
    perPage: Math.min(500, Math.max(10, int('perPage', d.perPage))),
  };
}

/**
 * Write filters back to the address bar, omitting anything at its default so
 * the common case stays a short, readable URL.
 */
export function toSearchParams(f: Filters, now?: Date): URLSearchParams {
  const d = makeDefaults(now);
  const sp = new URLSearchParams();
  if (f.q) sp.set('q', f.q);
  for (const s of f.status) sp.append('status', s);
  // The dates are written even when they match today's default. Omitting
  // them would make a link shared on Monday mean something else on Tuesday.
  sp.set('from', f.from);
  sp.set('to', f.to);
  if (f.dateField !== d.dateField) sp.set('dateField', f.dateField);
  if (f.type) sp.set('type', f.type);
  if (f.needadmin) sp.set('needadmin', '1');
  if (f.partnerId) sp.set('partnerId', f.partnerId);
  for (const id of f.services) sp.append('services', String(id));
  if (f.club) sp.set('club', f.club);
  if (f.source) sp.set('source', f.source);
  if (f.unpaidOnly) sp.set('unpaidOnly', '1');
  if (f.missingCarOnly) sp.set('missingCarOnly', '1');
  if (f.site) sp.set('site', f.site);
  if (f.sort !== d.sort) sp.set('sort', f.sort);
  if (f.dir !== d.dir) sp.set('dir', f.dir);
  if (f.page !== 1) sp.set('page', String(f.page));
  if (f.perPage !== d.perPage) sp.set('perPage', String(f.perPage));
  return sp;
}

/**
 * The query the API is actually sent.
 *
 * The grid sends the date bounds whenever they are set, search term or not,
 * which is what the old page did: Hledat looked inside the date box, and a
 * dispatcher who could not find a booking cleared the box (see parseFilters
 * for the one case — a link carrying only `q` — that arrives with no box to
 * clear). Statistiky passes `searchEverything`, because there a search term
 * over a bounded window draws a chart of one bar and says nothing.
 */
export function toQuery(f: Filters, opts: { searchEverything?: boolean } = {}): Record<string, unknown> {
  const dropDates = !!opts.searchEverything && isSearching(f);
  return {
    q: f.q || undefined,
    status: f.status.length ? f.status : undefined,
    from: dropDates ? undefined : f.from || undefined,
    to: dropDates ? undefined : f.to || undefined,
    dateField: f.dateField,
    type: f.type ? [f.type] : undefined,
    needadmin: f.needadmin || undefined,
    partnerId: f.partnerId || undefined,
    services: f.services.length ? f.services : undefined,
    club: f.club || undefined,
    source: f.source || undefined,
    unpaidOnly: f.unpaidOnly || undefined,
    /* Unlike the date bounds, this one SURVIVES a search. The dates are
       dropped because a pasted booking number is a question about the whole
       database rather than about the fortnight on screen; the yard is not the
       same kind of thing. "Find me the Octavia on Kněževes" is a search
       narrowed by a place on purpose, and silently widening it back to all
       five yards would answer a different question than the one asked. */
    site: f.site || undefined,
    missingCarOnly: f.missingCarOnly || undefined,
    sort: f.sort,
    dir: f.dir,
    page: f.page,
    perPage: f.perPage,
  };
}

export function isSearching(f: Filters): boolean {
  return f.q.trim().length > 0;
}

/** 1 … 12 13 14 … 28 — never more than nine cells wide. */
export function pageList(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('…');
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

/**
 * The `driveremail` column is free text and holds plenty of things that are
 * not addresses — blanks, dashes, phone numbers, the word "nemá". A mailto:
 * link on any of those is a dead link, and a dead link in a dispatcher's
 * hand is worse than plain text because it looks like it will work.
 */
export function isEmail(v: string | null | undefined): v is string {
  return !!v && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
}

/** Same caution for phones: at least nine digits and no stray words. */
export function isPhone(v: string | null | undefined): v is string {
  if (!v) return false;
  const t = v.trim();
  return t.replace(/\D/g, '').length >= 9 && /^[\d\s+()./-]+$/.test(t);
}

/** '2026-07-24 18:30:00' -> { d: '24. 7.', t: '18:30' } without Date(). */
export function splitDt(v: string | null): { d: string; t: string } {
  if (!v || v.length < 16) return { d: '—', t: '' };
  return { d: `${Number(v.slice(8, 10))}. ${Number(v.slice(5, 7))}.`, t: v.slice(11, 16) };
}

/**
 * '2026-07-24 13:32:00' -> 'dnes 13:32' when it falls on `today`, otherwise
 * '24. 7. 13:32'.
 *
 * The dashboard's "Přijelo" and "Odjelo" columns are almost always today, and
 * a column of identical dates is noise an operator has to read past. Comparing
 * the date as a string is deliberate: the value is already local time, and
 * putting it through Date() only invites a timezone to change it.
 */
export function dayDt(v: string | null, today: string): string {
  if (!v || v.length < 16) return '—';
  const { d, t } = splitDt(v);
  return v.slice(0, 10) === today ? `dnes ${t}` : `${d} ${t}`;
}

/**
 * Whole hours from `now` until a stored 'YYYY-MM-DD HH:MM:SS'.
 *
 * Negative when the moment has already passed. The sign is kept on purpose:
 * the transfer queue is where an overdue car shows up, and rendering -2 h as
 * "2 h" would make a car that should already be at the terminal look like one
 * there is still time for.
 */
export function hoursUntil(v: string | null, now: Date = new Date()): number | null {
  if (!v || v.length < 16) return null;
  const t = new Date(v.replace(' ', 'T')).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.round((t - now.getTime()) / 3_600_000);
}

/**
 * A span of milliseconds as '3 h 05 m' / '45 m'.
 *
 * Always the magnitude, never the sign — the caller has already decided
 * whether the words around it read "čeká" or "za", and a minus buried inside
 * the number is the kind of thing a dispatcher reads past at 03:00. The
 * minutes are zero-padded only when hours are present, so '45 m' does not
 * become the misleading '0 h 45 m'.
 */
export function durationHm(ms: number): string {
  const mins = Math.round(Math.abs(ms) / 60_000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h} h ${String(m).padStart(2, '0')} m` : `${m} m`;
}

/**
 * 'HH:MM' at a fixed offset from a stored 'YYYY-MM-DD HH:MM:SS'.
 *
 * Used for the transfer queue's "na místě do …" line, which is the departure
 * minus one hour. It goes through Date so the arithmetic crosses midnight
 * correctly — a car leaving at 00:30 has to be at the terminal at 23:30 the
 * previous evening, and string surgery on the hour field would print '-1:30'.
 */
export function hhmmOffset(v: string | null, offsetMinutes: number): string {
  if (!v || v.length < 16) return '—';
  const t = new Date(v.replace(' ', 'T'));
  if (!Number.isFinite(t.getTime())) return '—';
  t.setMinutes(t.getMinutes() + offsetMinutes);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(t.getHours())}:${p(t.getMinutes())}`;
}

/**
 * A shift's length, and whether that length is worth a second look.
 *
 * The rota is twelve hours. A close that lands well away from twelve is
 * almost always a split shift or a doubled uzávěrka, and either way the money
 * in that row belongs to a window that is not the one the reader assumes.
 * Ten and fourteen hours are the bounds because they clear the ordinary
 * handover slip — the two most common start hours in thirteen years of
 * history are 19:00 and 07:00, an hour either side of the rota, which already
 * produces shifts of eleven and thirteen hours in perfectly normal weeks.
 *
 * A zero or negative span is `odd` and prints as an em dash rather than as
 * '0 h 00 m': a shift of no length is corrupt data, and rendering it as a
 * number invites someone to read it as a very quiet night.
 *
 * This lives here rather than in the component because the thresholds are a
 * judgement about the business, not about layout, and because a threshold
 * nobody can execute is a threshold nobody can check.
 */
export function shiftDuration(minutes: number): { text: string; odd: boolean } {
  if (!Number.isFinite(minutes) || minutes <= 0) return { text: '—', odd: true };
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return {
    text: `${h} h ${String(m).padStart(2, '0')} m`,
    odd: minutes < 10 * 60 || minutes > 14 * 60,
  };
}

/** '2026-07-24 18:30:00' -> '24. 7. 2026 18:30'. Long form, for the detail. */
export function longDt(v: string | null): string {
  if (!v || v.length < 16) return '—';
  return `${Number(v.slice(8, 10))}. ${Number(v.slice(5, 7))}. ${v.slice(0, 4)} ${v.slice(11, 16)}`;
}

/**
 * Czech noun agreement for a count: one form for 1, another for 2–4, a third
 * for 0 and 5 and up.
 *
 * This is not a nicety. A screen that says "1 rezervací" reads to a Czech
 * speaker roughly the way "1 reservations" reads in English — it is the first
 * thing that makes software look like it was translated by somebody who was
 * not listening, and this application is replacing one the staff already
 * trust. Any place a number is printed immediately before a noun goes through
 * here.
 *
 * It lives in this file, rather than beside the other display vocabulary in
 * ./format, for the reason everything else here does: format.ts imports
 * @smile/shared and so cannot be loaded by a harness with no packages
 * installed, and a rule about which of three forms a word takes is exactly
 * the kind of thing that should be executed by an assertion rather than
 * checked by eye. format.ts re-exports it and names the three words this
 * application actually counts.
 *
 * Only the nominative is handled, because that is the case a bare "N noun"
 * phrase takes. A sentence that continues past the noun — "412 vozů, které
 * odjely" — also has to agree its verb and pronoun, and no helper can do that
 * from the outside; those are written out longhand where they occur.
 *
 * Non-integers fall to the many-form. '1,5 dne' is a different construction
 * altogether (the genitive singular), and no count in this application is
 * fractional, so the safe answer is the form that reads least wrongly rather
 * than a form invented for a case that does not arise.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  if (!Number.isInteger(n)) return many;
  if (n === 1) return one;
  return n >= 2 && n <= 4 ? few : many;
}
